import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Plus, Edit2, Trash2, Search, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

export default function AdminProducts() {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<any>({
    type: 0,
    price: '',
    stock: 0,
    categoryId: '',
    imageUrl: ''
  });
  const [isUploading, setIsUploading] = useState(false);

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await axios.get('http://localhost:5000/api/categories');
      return data;
    }
  });

  const { data: productsData, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const { data } = await axios.get('http://localhost:5000/api/products?pageSize=100');
      return data;
    }
  });

  const products = (productsData?.items || []).filter((p: any) => p.isActive);

  const createMutation = useMutation({
    mutationFn: async (product: any) => {
      const token = localStorage.getItem('access_token');
      await axios.post('http://localhost:5000/api/products', product, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsAdding(false);
      setFormData({ type: 0, price: '', stock: 0, categoryId: '' });
      toast.success('Ürün başarıyla eklendi!');
    },
    onError: (e: any) => {
      toast.error('Kayıt başarısız: ' + (e.response?.data?.error || e.message));
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('access_token');
      await axios.delete(`http://localhost:5000/api/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Ürün silindi!');
    },
    onError: (e: any) => {
      toast.error('Silme başarısız: ' + (e.response?.data?.error || e.message));
    }
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const formDataPayload = new FormData();
    formDataPayload.append('file', file);

    setIsUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.post('http://localhost:5000/api/upload', formDataPayload, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setFormData({ ...formData, imageUrl: `http://localhost:5000${data.url}` });
      toast.success('Resim başarıyla yüklendi!');
    } catch (err: any) {
      toast.error('Resim yüklenemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.categoryId) {
      toast.error('Lütfen bir kategori seçin!');
      return;
    }

    // Derive type from selected category
    const selectedCat = categories.find((c: any) => c.id === formData.categoryId);
    const derivedType = selectedCat ? selectedCat.type : 0;

    createMutation.mutate({
      ...formData,
      type: derivedType,
      price: Number(formData.price),
      stock: Number(formData.stock),
      sortOrder: 0,
      preparationTimeMinutes: 15,
      isActive: true
    });
  };

  if (isLoading) return <div className="p-8"><div className="skeleton h-64 w-full"></div></div>;

  return (
    <div className="p-6 animate-fade-in">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Ürün Yönetimi</h1>
          <p className="text-muted mt-2">Menünüzdeki yemekleri ve market ürünlerini yönetin.</p>
        </div>
        <button onClick={() => setIsAdding(true)} className="btn btn-primary" disabled={categories.length === 0}>
          <Plus size={20} /> Yeni Ürün
        </button>
      </div>
      
      {categories.length === 0 && (
        <div className="bg-warning/20 text-warning p-4 rounded-lg mb-6 flex justify-between items-center">
          <span>Ürün ekleyebilmek için önce kategori oluşturmalısınız.</span>
          <Link to="/admin/categories" className="btn btn-sm btn-outline btn-warning">Kategorilere Git</Link>
        </div>
      )}

      {isAdding && categories.length > 0 && (
        <div className="card mb-8">
          <h3 className="font-bold mb-4">Yeni Ürün Ekle</h3>
          <form onSubmit={handleSave} className="grid-2 gap-4">
            <div className="form-group col-span-2">
              <label className="form-label">Ürün Adı</label>
              <input required type="text" className="form-input" placeholder="Örn: Karışık Pizza" 
                value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} />
            </div>
            
            <div className="form-group">
              <label className="form-label">Kategori</label>
              <select required className="form-input" value={formData.categoryId} onChange={e => setFormData({...formData, categoryId: e.target.value})}>
                <option value="" disabled>Seçiniz...</option>
                {categories.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.type === 1 ? 'Yemek' : 'Market'})</option>
                ))}
              </select>
            </div>


            <div className="form-group">
              <label className="form-label">Fiyat (₺)</label>
              <input required type="number" className="form-input" placeholder="120" step="0.01"
                value={formData.price || ''} onChange={e => setFormData({...formData, price: e.target.value})} />
            </div>

            <div className="form-group">
              <label className="form-label">Stok</label>
              <input required type="number" className="form-input" placeholder="100" 
                value={formData.stock || ''} onChange={e => setFormData({...formData, stock: e.target.value})} />
            </div>

            <div className="form-group col-span-2">
              <label className="form-label">Resim</label>
              <div className="flex gap-4 items-center">
                {formData.imageUrl ? (
                  <img src={formData.imageUrl} alt="Preview" className="w-16 h-16 object-cover rounded shadow" />
                ) : (
                  <div className="w-16 h-16 bg-surface-2 border border-dashed border-border rounded flex items-center justify-center text-muted">
                    <ImageIcon size={24} />
                  </div>
                )}
                <div className="flex-1">
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="imageUpload" />
                  <label htmlFor="imageUpload" className="btn btn-outline cursor-pointer mb-2 inline-block">
                    {isUploading ? 'Yükleniyor...' : 'Bilgisayardan Resim Seç'}
                  </label>
                  <p className="text-xs text-muted">Veya doğrudan URL girebilirsiniz:</p>
                  <input type="text" className="form-input mt-1" placeholder="https://..." 
                    value={formData.imageUrl || ''} onChange={e => setFormData({...formData, imageUrl: e.target.value})} />
                </div>
              </div>
            </div>

            <div className="form-group col-span-2">
              <label className="form-label">Açıklama (Opsiyonel)</label>
              <textarea className="form-input" placeholder="İçindekiler vb." rows={2}
                value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})}></textarea>
            </div>
            
            <div className="col-span-2 flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setIsAdding(false)} className="btn btn-ghost">İptal</button>
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>Kaydet</button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-surface-2 border-b border-border">
              <th className="p-4 font-semibold text-sm w-16">Resim</th>
              <th className="p-4 font-semibold text-sm">Ürün Adı</th>
              <th className="p-4 font-semibold text-sm">Kategori</th>
              <th className="p-4 font-semibold text-sm">Tip</th>
              <th className="p-4 font-semibold text-sm">Fiyat (₺)</th>
              <th className="p-4 font-semibold text-sm">Stok</th>
              <th className="p-4 font-semibold text-sm text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p: any) => (
              <tr key={p.id} className="border-b border-border hover:bg-surface-2 transition">
                <td className="p-4">
                  {p.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt={p.name}
                      style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{ width: '44px', height: '44px', borderRadius: '8px', flexShrink: 0 }} className="bg-surface border border-border flex items-center justify-center text-muted">
                      <ImageIcon size={16} />
                    </div>
                  )}
                </td>
                <td className="p-4 font-medium">{p.name}</td>
                <td className="p-4 text-sm text-muted">{categories.find((c: any) => c.id === p.categoryId)?.name || 'Bilinmiyor'}</td>
                <td className="p-4 text-muted text-sm">
                  {p.type === 1 ? 'Yemek' : p.type === 2 ? 'Market' : '-'}
                </td>
                <td className="p-4 font-bold text-primary">{p.price} ₺</td>
                <td className="p-4">
                  <span className={`badge ${p.stock > 0 ? 'badge-neutral' : 'badge-error text-white'}`}>
                    {p.stock > 0 ? p.stock : 'Tükendi'}
                  </span>
                </td>
                <td className="p-4 text-right flex gap-2 justify-end">
                  <button 
                    onClick={() => { if(confirm('Ürünü silmek istediğinize emin misiniz?')) deleteMutation.mutate(p.id); }} 
                    className="btn btn-sm btn-ghost text-error"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-muted">Henüz ürün eklenmemiş.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
